import { useMutation } from "@apollo/client";
import {
  SavedView,
  SavedViewPage,
  SavedViewFilterInput,
  SavedViewSortInput,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import {
  CREATE_SAVED_VIEW,
  FULL_UPDATE_SAVED_VIEW,
  DELETE_SAVED_VIEW,
  SET_DEFAULT_SAVED_VIEW,
} from "../graphql";
import {
  CreateSavedViewMutationResponse,
  CreateSavedViewMutationVariables,
  FullUpdateSavedViewMutationResponse,
  FullUpdateSavedViewMutationVariables,
  DeleteSavedViewMutationResponse,
  DeleteSavedViewMutationVariables,
  SetDefaultSavedViewMutationResponse,
  SetDefaultSavedViewMutationVariables,
} from "../types";

interface UseSavedViewsMutationsProps {
  page: SavedViewPage;
  onCreateSuccess: (view: SavedView) => void;
  onUpdateSuccess: (view: SavedView) => void;
  onDeleteSuccess: (deletedViewId: string) => void;
}

export function useSavedViewsMutations({
  page,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
}: UseSavedViewsMutationsProps) {
  const showError = useErrorToast();
  const { courierId, employeeId, courieUser } = useAuth();
  const userOrEmployeeId = employeeId || courieUser?.id;

  const [createMutation] = useMutation<
    CreateSavedViewMutationResponse,
    CreateSavedViewMutationVariables
  >(CREATE_SAVED_VIEW, {
    onError: (error) => showError(error),
    fetchPolicy: "no-cache",
  });

  const [updateMutation] = useMutation<
    FullUpdateSavedViewMutationResponse,
    FullUpdateSavedViewMutationVariables
  >(FULL_UPDATE_SAVED_VIEW, {
    onError: (error) => showError(error),
    fetchPolicy: "no-cache",
  });

  const [deleteMutation] = useMutation<
    DeleteSavedViewMutationResponse,
    DeleteSavedViewMutationVariables
  >(DELETE_SAVED_VIEW, {
    onError: (error) => showError(error),
    fetchPolicy: "no-cache",
  });

  const [setDefaultMutation] = useMutation<
    SetDefaultSavedViewMutationResponse,
    SetDefaultSavedViewMutationVariables
  >(SET_DEFAULT_SAVED_VIEW, {
    onError: (error) => showError(error),
    fetchPolicy: "no-cache",
  });

  const createSavedView = async (
    name: string,
    primaryFilter: string | null,
    filters: SavedViewFilterInput[],
    sortFields: SavedViewSortInput[]
  ): Promise<void> => {
    if (!courierId || !userOrEmployeeId) return;

    return createMutation({
      variables: {
        courierId,
        userOrEmployeeId,
        page,
        name,
        primaryFilter,
        filters,
        sortFields,
      },
    }).then((result) => {
      if (result.data?.createSavedView) {
        onCreateSuccess(result.data.createSavedView);
      }
    });
  };

  const updateSavedView = async (
    id: string,
    name: string,
    primaryFilter: string | null,
    filters: SavedViewFilterInput[],
    sortFields: SavedViewSortInput[]
  ): Promise<void> => {
    if (!courierId) return;

    return updateMutation({
      variables: {
        courierId,
        id,
        name,
        primaryFilter,
        filters,
        sortFields,
      },
    }).then((result) => {
      if (result.data?.fullUpdateSavedView) {
        onUpdateSuccess(result.data.fullUpdateSavedView);
      }
    });
  };

  const deleteSavedView = async (savedViewId: string): Promise<void> => {
    if (!courierId || !userOrEmployeeId) return;

    return deleteMutation({
      variables: {
        courierId,
        userOrEmployeeId,
        savedViewId,
      },
    }).then((result) => {
      if (result.data?.deleteSavedView) {
        onDeleteSuccess(savedViewId);
      }
    });
  };

  const setDefaultSavedView = async (savedViewId: string): Promise<void> => {
    if (!courierId || !userOrEmployeeId) return;

    return setDefaultMutation({
      variables: {
        courierId,
        userOrEmployeeId,
        page,
        savedViewId,
      },
    }).then(() => {});
  };

  return {
    createSavedView,
    updateSavedView,
    deleteSavedView,
    setDefaultSavedView,
  };
}
